import React, { createContext, useContext } from "react";
import { useCrewLiveEventSource } from "./useCrewLiveEventSource";
import { PlaylistLivePositionPayload } from "./playlist/PlaylistLivePositionPayload";
import { usePlaylistLivePositionFromEventSource } from "../playlist/usePlaylistLivePositionFromEventSource";
import { useDiscoverFromEventSource } from "./discover/useDiscoverFromEventSource";

export interface CrewLiveContextType {
  /**
   * Each context, that is part of crew live events, should - if he has beneficial information to share - handle
   * the discovery request by sending its current own context to the server (whatever type of context it is).
   *
   * The counter can just be use as a dependency to trigger sending whenever the counter changes.
   *
   * As there can be multiple consumers of this context, it is sufficient, if the most appropriate one handles the discovery request.
   * Example: Currently we have PlaylistBanner and SongsheetCarousel as consumers of this context. All consume but only SongsheetCarousel has beneficial information to share.
   *
   * Unsolved question (as not needed yet): If multiple consumers have different information to share, how to handle that? Currently, the first handler will set the event to handled --> no other consumer will respond.
   *
   */
  discoverDependencyCounter: number;

  currentCrewIdentifier: string | undefined;
  eventSource: EventSource | undefined;
  playlistLivePosition: ReadonlyMap<string, PlaylistLivePositionPayload>;
  playlistLivePositionEventSourceHandlerIsReady: boolean;
  devicesOnline: readonly string[];
}

export const CrewLiveContext: React.Context<CrewLiveContextType | undefined> =
  createContext<CrewLiveContextType | undefined>(undefined);

export function useCrewLiveContextStateProvider(
  crewId: string | undefined,
): CrewLiveContextType {
  const { eventSource } = useCrewLiveEventSource(crewId);

  const { discoverDependencyCounter } = useDiscoverFromEventSource(eventSource);

  const {
    playlistLivePosition,
    playlistLivePositionEventSourceHandlerIsReady,
    devicesOnline,
  } = usePlaylistLivePositionFromEventSource(eventSource);

  return {
    discoverDependencyCounter,
    currentCrewIdentifier: crewId,
    eventSource,
    playlistLivePosition,
    playlistLivePositionEventSourceHandlerIsReady,
    devicesOnline,
  };
}

// Hack to never have undefined in the context
// https://stackoverflow.com/a/69735347
export const useCrewLiveContext: () => CrewLiveContextType = () => {
  const c = useContext(CrewLiveContext);
  if (!c) throw new Error("No provider found.");
  return c;
};
